import { toast as hotToast } from 'react-hot-toast';

interface CustomToastProps {
  message: string;
  type: 'success' | 'error' | 'loading' | 'default';
  toastId: string;
}

export const CustomToast = ({ message, type, toastId }: CustomToastProps) => {
  const getStyles = () => {
    const baseStyle = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      width: '100%',
    };

    switch (type) {
      case 'success':
        return {
          ...baseStyle,
          background: 'rgba(85, 239, 196, 0.85)',
          color: '#00684A',
          border: '1px solid rgba(85, 239, 196, 0.8)',
        };
      case 'error':
        return {
          ...baseStyle,
          background: 'rgba(255, 107, 129, 0.85)',
          color: '#8B0000',
          border: '1px solid rgba(255, 107, 129, 0.8)',
        };
      case 'loading':
        return {
          ...baseStyle,
          background: 'rgba(116, 185, 255, 0.85)',
          color: '#0652DD',
          border: '1px solid rgba(116, 185, 255, 0.8)',
        };
      default:
        return {
          ...baseStyle,
          background: 'rgba(255, 255, 255, 0.85)',
          color: '#2d3436',
          border: '1px solid rgba(255, 255, 255, 0.6)',
        };
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'error':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'loading':
        return (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="animate-spin"
          >
            <path
              fillRule="evenodd"
              d="M10 3a7 7 0 100 14 7 7 0 000-14zm0-2a9 9 0 110 18 9 9 0 010-18z"
              clipRule="evenodd"
              opacity="0.25"
            />
            <path d="M10 1a9 9 0 019 9h-2a7 7 0 00-7-7V1z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        ...getStyles(),
        padding: '20px 24px',
        borderRadius: '16px',
        fontSize: '16px',
        fontWeight: '500',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        minWidth: '320px',
        maxWidth: '500px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
        {getIcon()}
        <span>{message}</span>
      </div>

      {/* Close button */}
      <button
        onClick={() => hotToast.remove(toastId)}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4px',
          opacity: 0.7,
          transition: 'opacity 0.2s',
          color: 'inherit',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.7';
        }}
        aria-label="Close notification"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  );
};

// Helper functions to use custom toasts
export const toast = {
  success: (message: string) => {
    return hotToast.custom(
      (t) => <CustomToast message={message} type="success" toastId={t.id} />,
      { duration: 4000 }
    );
  },
  error: (message: string) => {
    return hotToast.custom(
      (t) => <CustomToast message={message} type="error" toastId={t.id} />,
      { duration: 4000 }
    );
  },
  loading: (message: string) => {
    return hotToast.custom(
      (t) => <CustomToast message={message} type="loading" toastId={t.id} />,
      { duration: 4000 }
    );
  },
  message: (message: string) => {
    return hotToast.custom(
      (t) => <CustomToast message={message} type="default" toastId={t.id} />,
      { duration: 4000 }
    );
  },
};
